import {
  ChangeEmailInitiateResultTypenames,
  Mutation,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import { useContext, useState } from 'react';
import ChangeEmailInitiate from 'services/graphql/mutations/ChangeEmailInitiate';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { useMutation } from 'urql';

const useChangeEmail = () => {
  const router = useRouter();
  const [errorScreen, setErrorScreen] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [emailError, setEmailError] = useState<string>('');

  const { setUpdatedEmailId, setLastFourPhoneDigits } =
    useContext(MyVaroContext);
  const [{ fetching }, executeMutation] =
    useMutation<Mutation>(ChangeEmailInitiate);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const confirmNewEmailAddress = async (newEmail: string) => {
    try {
      const { data, error } = await executeMutation({ input: { newEmail } });

      if (error) {
        throw error;
      }
      const responseData = data?.changeEmailInitiate;

      if (
        responseData?.__typename ===
        ChangeEmailInitiateResultTypenames.ChangeEmailInitiateSuccess
      ) {
        setUpdatedEmailId?.(newEmail);

        if (typeof responseData.customerInfo?.phone === 'string') {
          setLastFourPhoneDigits?.(responseData.customerInfo.phone.slice(-4));
        }

        router.push('/myvaro/personal-details/change-email/enter-code');
      } else if (
        responseData?.__typename ===
          ChangeEmailInitiateResultTypenames.ChangeEmailTooManyAttempts ||
        responseData?.__typename ===
          ChangeEmailInitiateResultTypenames.ChangeEmailError
      ) {
        setErrorScreen({
          title: responseData.title,
          message: responseData.message,
        });
      }
    } catch (error) {
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }

      setErrorScreen({
        title: errorTitle,
        message: errorMessage,
      });

      logErrorWithContext(error, {
        featureArea: FeatureArea.CHANGE_EMAIL,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.LOW,
      });
    }
  };

  return {
    isLoading: fetching,
    errorScreen,
    emailError,
    setEmailError,
    setErrorScreen,
    confirmNewEmailAddress,
  };
};

export default useChangeEmail;
